// Admin LLM config page.
//
// Flow:
//   1. Click "Add config" → fill provider + key in dialog
//   2. Click "Fetch models" → calls POST /api/admin/llm-configs/list-models,
//      populates the model dropdown
//   3. Pick model + click Save → POSTs to /api/admin/llm-configs (optionally
//      with set_active=true)
//   4. List below shows saved configs with masked keys, active badge,
//      test-connection button, mark-active button, delete button.

import { useEffect, useState } from 'react';
import { CheckCircle2, Trash2, AlertCircle, Plus, Key, RefreshCw } from 'lucide-react';
import { api, ApiError } from '../api';
import { Shell } from '../components/layout/Shell';
import { PageHeader } from '../components/layout/PageHeader';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { EmptyState } from '../components/ui/empty-state';
import { useToast } from '../lib/toast';
import { fmtRelative } from '../lib/utils';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

type LlmProvider = 'anthropic' | 'openai' | 'google';
type LlmPurpose = 'outreach_drafting' | 'reply_handling';

interface LlmConfigPublic {
  id: string;
  provider: LlmProvider;
  api_key_last4: string;
  base_url: string | null;
  selected_model: string;
  purpose: LlmPurpose;
  display_name: string | null;
  is_active: boolean;
  last_tested_at: string | null;
  last_test_ok: boolean | null;
  last_test_error: string | null;
  created_at: string;
  updated_at: string;
}

interface LlmModel {
  id: string;
  display_name?: string;
}

const PROVIDER_LABEL: Record<LlmProvider, string> = {
  anthropic: 'Anthropic (Claude)',
  openai: 'OpenAI',
  google: 'Google (Gemini)',
};
const PURPOSE_LABEL: Record<LlmPurpose, string> = {
  outreach_drafting: 'Outreach drafting',
  reply_handling: 'Reply handling',
};

export function LlmConfigPage() {
  const [rows, setRows] = useState<LlmConfigPublic[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const { show: toast } = useToast();

  function load() {
    setRows(null);
    setErr(null);
    api<{ configs: LlmConfigPublic[] }>('/api/admin/llm-configs')
      .then((r) => setRows(r.configs))
      .catch((e) => setErr(e instanceof ApiError ? `Error ${e.status}` : 'Could not load'));
  }
  useEffect(load, []);

  async function testConnection(id: string) {
    setBusyId(id);
    try {
      await api(`/api/admin/llm-configs/${id}/test`, { method: 'POST' });
      toast('Connection OK', 'success');
      load();
    } catch (e) {
      const msg = e instanceof ApiError ? (e.body as { error?: string })?.error ?? 'Test failed' : 'Test failed';
      toast(msg, 'error');
      load();
    } finally {
      setBusyId(null);
    }
  }

  async function setActive(id: string) {
    setBusyId(id);
    try {
      await api(`/api/admin/llm-configs/${id}`, {
        method: 'PATCH',
        json: { is_active: true },
      });
      toast('Marked active', 'success');
      load();
    } catch (e) {
      toast(e instanceof ApiError ? `Error ${e.status}` : 'Failed', 'error');
    } finally {
      setBusyId(null);
    }
  }

  async function remove(id: string) {
    if (!confirm('Delete this LLM config?')) return;
    setBusyId(id);
    try {
      await api(`/api/admin/llm-configs/${id}`, { method: 'DELETE' });
      toast('Deleted', 'success');
      load();
    } catch (e) {
      toast(e instanceof ApiError ? `Error ${e.status}` : 'Failed', 'error');
    } finally {
      setBusyId(null);
    }
  }

  return (
    <Shell title="LLM Config">
      <PageHeader
        title="LLM config"
        description="Pluggable LLM provider for outreach drafting and (later) reply handling."
        actions={
          <Button onClick={() => setAddOpen(true)}>
            <Plus className="h-4 w-4" /> Add config
          </Button>
        }
      />

      {err && (
        <Card className="mb-4 border-error/40 bg-error-tint">
          <CardContent className="py-3 text-sm text-error-on-tint">{err}</CardContent>
        </Card>
      )}

      <div className="space-y-3">
        {!rows &&
          Array.from({ length: 2 }).map((_, i) => (
            <Card key={i}>
              <CardContent className="p-4">
                <Skeleton className="h-16 w-full" />
              </CardContent>
            </Card>
          ))}

        {rows && rows.length === 0 && (
          <Card>
            <CardContent>
              <EmptyState
                icon={Key}
                title="No LLM configs yet."
                description="Add a config to enable outreach draft generation."
              />
            </CardContent>
          </Card>
        )}

        {rows?.map((r) => (
          <Card key={r.id}>
            <CardContent className="flex flex-col gap-3 p-4 md:flex-row md:items-center">
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-semibold text-text">
                    {r.display_name ?? PROVIDER_LABEL[r.provider]}
                  </span>
                  <Badge variant={r.is_active ? 'completed' : 'default'}>
                    {r.is_active ? 'Active' : 'Inactive'}
                  </Badge>
                  <Badge variant="default">{PURPOSE_LABEL[r.purpose]}</Badge>
                  {r.last_test_ok === true && (
                    <Badge variant="open">
                      <CheckCircle2 className="h-3 w-3" /> Tested OK
                    </Badge>
                  )}
                  {r.last_test_ok === false && (
                    <Badge variant="urgent">
                      <AlertCircle className="h-3 w-3" /> Test failed
                    </Badge>
                  )}
                </div>
                <p className="mt-1 text-xs text-text-muted">
                  {PROVIDER_LABEL[r.provider]} · model: <code className="rounded bg-surface-muted px-1.5 py-0.5">{r.selected_model}</code>
                  {' · '}
                  key: <code className="rounded bg-surface-muted px-1.5 py-0.5">…{r.api_key_last4}</code>
                  {r.last_tested_at && ` · tested ${fmtRelative(r.last_tested_at)}`}
                </p>
                {r.base_url && (
                  <p className="mt-1 truncate text-xs text-text-muted">
                    endpoint: <code className="rounded bg-surface-muted px-1.5 py-0.5">{r.base_url}</code>
                  </p>
                )}
                {r.last_test_error && (
                  <p className="mt-1 truncate text-xs text-error-on-tint">{r.last_test_error}</p>
                )}
              </div>
              <div className="flex shrink-0 flex-wrap gap-2 md:flex-col md:items-end">
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={busyId === r.id}
                  onClick={() => testConnection(r.id)}
                >
                  <RefreshCw className="h-3.5 w-3.5" /> Test
                </Button>
                {!r.is_active && (
                  <Button
                    variant="primary"
                    size="sm"
                    disabled={busyId === r.id}
                    onClick={() => setActive(r.id)}
                  >
                    Make active
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-error"
                  disabled={busyId === r.id}
                  onClick={() => remove(r.id)}
                >
                  <Trash2 className="h-3.5 w-3.5" /> Delete
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <AddConfigDialog open={addOpen} onClose={() => setAddOpen(false)} onSaved={load} />
    </Shell>
  );
}

// ---------- Add dialog ----------------------------------------------------

interface AddDialogProps {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
}

function AddConfigDialog({ open, onClose, onSaved }: AddDialogProps) {
  const [provider, setProvider] = useState<LlmProvider>('anthropic');
  const [apiKey, setApiKey] = useState('');
  const [baseUrl, setBaseUrl] = useState('');
  const [purpose, setPurpose] = useState<LlmPurpose>('outreach_drafting');
  const [displayName, setDisplayName] = useState('');
  const [models, setModels] = useState<LlmModel[] | null>(null);
  const [selectedModel, setSelectedModel] = useState('');
  // Some proxies (Moses, LM Studio, custom gateways) don't implement the
  // provider's /models endpoint. Toggle this to bypass Fetch Models and
  // type the model id directly.
  const [manualModel, setManualModel] = useState(false);
  const [setActiveOnSave, setSetActiveOnSave] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const { show: toast } = useToast();

  function reset() {
    setProvider('anthropic');
    setApiKey('');
    setBaseUrl('');
    setPurpose('outreach_drafting');
    setDisplayName('');
    setModels(null);
    setSelectedModel('');
    setManualModel(false);
    setSetActiveOnSave(true);
    setErr(null);
  }

  async function fetchModels() {
    setErr(null);
    setBusy(true);
    setModels(null);
    setSelectedModel('');
    try {
      const r = await api<{ ok: boolean; models?: LlmModel[]; error?: string }>(
        '/api/admin/llm-configs/list-models',
        {
          method: 'POST',
          json: {
            provider,
            api_key: apiKey,
            ...(baseUrl.trim() ? { base_url: baseUrl.trim() } : {}),
          },
        },
      );
      if (r.ok && r.models) {
        setModels(r.models);
        if (r.models.length > 0) setSelectedModel(r.models[0]!.id);
      } else {
        setErr(r.error ?? 'Could not fetch models');
      }
    } catch (e) {
      const msg = e instanceof ApiError ? (e.body as { error?: string })?.error ?? `Error ${e.status}` : 'Network error';
      setErr(msg);
    } finally {
      setBusy(false);
    }
  }

  async function save() {
    setErr(null);
    setBusy(true);
    try {
      await api('/api/admin/llm-configs', {
        method: 'POST',
        json: {
          provider,
          api_key: apiKey,
          ...(baseUrl.trim() ? { base_url: baseUrl.trim() } : {}),
          selected_model: selectedModel,
          purpose,
          display_name: displayName || undefined,
          set_active: setActiveOnSave,
        },
      });
      toast('Config saved', 'success');
      reset();
      onSaved();
      onClose();
    } catch (e) {
      const msg = e instanceof ApiError ? `Error ${e.status}` : 'Save failed';
      setErr(msg);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) { reset(); onClose(); } }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Add LLM config</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid gap-1.5">
            <Label>Provider</Label>
            <Select value={provider} onValueChange={(v) => { setProvider(v as LlmProvider); setModels(null); }}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="anthropic">Anthropic (Claude)</SelectItem>
                <SelectItem value="openai">OpenAI</SelectItem>
                <SelectItem value="google">Google (Gemini)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="grid gap-1.5">
            <Label>API key</Label>
            <Input
              type="password"
              value={apiKey}
              onChange={(e) => { setApiKey(e.target.value); setModels(null); }}
              placeholder={
                provider === 'anthropic' ? 'sk-ant-…'
                : provider === 'openai' ? 'sk-…'
                : 'AIza…'
              }
              autoComplete="off"
            />
          </div>

          <div className="grid gap-1.5">
            <Label>Base URL (optional)</Label>
            <Input
              type="url"
              value={baseUrl}
              onChange={(e) => { setBaseUrl(e.target.value); setModels(null); }}
              placeholder={
                provider === 'anthropic' ? 'leave blank for api.anthropic.com'
                : provider === 'openai' ? 'leave blank for api.openai.com — or e.g. https://openrouter.ai/api/v1'
                : 'leave blank for generativelanguage.googleapis.com'
              }
              autoComplete="off"
            />
            <p className="text-[11px] text-text-muted">
              Override the default endpoint. Useful for OpenAI-compatible proxies
              (OpenRouter, DeepSeek, Together, Groq, LM Studio, Moses) or Anthropic
              gateways. Leave blank for official APIs.
            </p>
          </div>

          <div className="grid gap-1.5">
            <Label>Purpose</Label>
            <Select value={purpose} onValueChange={(v) => setPurpose(v as LlmPurpose)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="outreach_drafting">Outreach drafting</SelectItem>
                <SelectItem value="reply_handling">Reply handling</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="grid gap-1.5">
            <Label>Display name (optional)</Label>
            <Input
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="e.g. Claude Sonnet 4.6 (outreach)"
            />
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {!manualModel && (
              <Button
                type="button"
                variant="secondary"
                disabled={!apiKey || busy}
                onClick={fetchModels}
              >
                <RefreshCw className="h-4 w-4" /> Fetch models
              </Button>
            )}
            <button
              type="button"
              className="text-xs text-text-muted underline-offset-2 hover:text-text hover:underline"
              onClick={() => {
                setManualModel((v) => !v);
                setModels(null);
                setSelectedModel('');
                setErr(null);
              }}
            >
              {manualModel ? '← back to fetched list' : 'enter model id manually →'}
            </button>
          </div>

          {manualModel ? (
            <div className="grid gap-1.5">
              <Label>Model id</Label>
              <Input
                value={selectedModel}
                onChange={(e) => setSelectedModel(e.target.value.trim())}
                placeholder={
                  provider === 'anthropic' ? 'e.g. claude-sonnet-4-6'
                  : provider === 'openai' ? 'e.g. gpt-4o or moonshot/kimi-k2'
                  : 'e.g. gemini-1.5-pro'
                }
                autoComplete="off"
              />
              <p className="text-[11px] text-text-muted">
                Use this when the proxy doesn't expose a model list (Moses,
                LM Studio, some gateways). The exact id depends on what the
                endpoint accepts in its completion requests.
              </p>
            </div>
          ) : (
            models && (
              <div className="grid gap-1.5">
                <Label>Model</Label>
                <Select value={selectedModel} onValueChange={setSelectedModel}>
                  <SelectTrigger>
                    <SelectValue placeholder="Pick a model" />
                  </SelectTrigger>
                  <SelectContent>
                    {models.map((m) => (
                      <SelectItem key={m.id} value={m.id}>
                        {m.display_name && m.display_name !== m.id
                          ? `${m.display_name} (${m.id})`
                          : m.id}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )
          )}

          <label className="flex items-center gap-2 text-sm text-text">
            <input
              type="checkbox"
              checked={setActiveOnSave}
              onChange={(e) => setSetActiveOnSave(e.target.checked)}
            />
            Set as active for this purpose immediately
          </label>

          {err && (
            <p className="rounded-md border border-error/40 bg-error-tint px-3 py-2 text-xs text-error-on-tint">
              {err}
            </p>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => { reset(); onClose(); }} disabled={busy}>
            Cancel
          </Button>
          <Button onClick={save} disabled={busy || !selectedModel}>
            Save config
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
